import { useCallback, useEffect, useState } from "react";
import { Activity, AlertTriangle, CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import Reveal from "@/components/Reveal";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";

const META = {
  operational: { color: "text-emerald-400", dot: "bg-emerald-400", ring: "border-emerald-400/30 bg-emerald-400/5", Icon: CheckCircle2 },
  degraded: { color: "text-amber-400", dot: "bg-amber-400", ring: "border-amber-400/30 bg-amber-400/5", Icon: AlertTriangle },
  down: { color: "text-[#ED1C24]", dot: "bg-[#ED1C24]", ring: "border-[#ED1C24]/40 bg-[#ED1C24]/10", Icon: XCircle },
};

export default function ServiceStatus() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [checkedAt, setCheckedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState(false);

  const STATUS_LABEL = {
    operational: t("status_operational"),
    degraded: t("status_degraded"),
    down: t("status_down"),
  };
  const OVERALL_LABEL = {
    operational: t("status_all_ok"),
    degraded: t("status_partial"),
    down: t("status_outage"),
  };

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data: d } = await api.get("/service-status");
      setData(d);
      setCheckedAt(new Date());
      setFailed(false);
    } catch { setFailed(true); } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const timer = setInterval(() => load(true), 60000);
    return () => clearInterval(timer);
  }, [load]);

  const overall = META[data?.overall] || META.operational;
  const OverallIcon = overall.Icon;

  return (
    <main data-testid="status-page" className="min-h-screen bg-[#0B1120] px-6 md:px-12 lg:px-24 pt-32 pb-24">
      <Reveal y={24}>
        <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-4">{t("status_label")}</p>
        <h1 className="font-display font-semibold text-white text-4xl sm:text-6xl tracking-tight mb-4">
          {t("status_title")}<span className="text-[#FFC61A]">.</span>
        </h1>
        <p className="text-slate-400 max-w-xl mb-12">{t("status_sub")}</p>
      </Reveal>

      <div className="max-w-4xl mx-auto">
        <Reveal delay={0.05}>
          <div
            data-testid="status-overall-banner"
            className={`border ${overall.ring} p-6 flex items-center gap-4 mb-8`}
          >
            <OverallIcon size={28} className={`${overall.color} shrink-0`} />
            <div className="flex-1">
              <p data-testid="status-overall-text" className={`font-display font-semibold text-xl ${overall.color}`}>
                {OVERALL_LABEL[data?.overall] || OVERALL_LABEL.operational}
              </p>
              {checkedAt && (
                <p className="text-xs text-slate-500 mt-1">
                  {t("status_last_checked")} {checkedAt.toLocaleTimeString()}
                </p>
              )}
            </div>
            <button
              data-testid="status-refresh-btn"
              onClick={() => load()}
              className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#ED1C24] transition-colors duration-300"
              aria-label="Refresh status"
            >
              <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            </button>
          </div>
        </Reveal>

        <div className="border border-white/10 divide-y divide-white/10">
          {(data?.services || []).map((s, i) => {
            const meta = META[s.status] || META.operational;
            return (
              <Reveal key={s.name} delay={0.08 + i * 0.06}>
                <div
                  data-testid={`status-service-row-${i}`}
                  className="flex flex-wrap items-center gap-4 px-6 py-5 bg-[#0F172A]/60 hover:bg-[#0F172A] transition-colors duration-300"
                >
                  <span className="relative flex h-3 w-3 shrink-0">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${meta.dot} opacity-40`} />
                    <span className={`relative inline-flex rounded-full h-3 w-3 ${meta.dot}`} />
                  </span>
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-display font-semibold text-white text-lg">{s.name}</p>
                    {s.note && <p className="text-sm text-slate-400 mt-0.5">{s.note}</p>}
                  </div>
                  <div className="text-right">
                    <span
                      data-testid={`status-badge-${i}`}
                      className={`inline-flex items-center gap-1.5 text-xs font-medium tracking-wide uppercase ${meta.color}`}
                    >
                      <meta.Icon size={14} />
                      {STATUS_LABEL[s.status] || s.status}
                    </span>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {t("status_updated")}:{" "}
                      {s.updated_at ? new Date(s.updated_at).toLocaleString() : t("status_never")}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
          {!data && !failed && (
            <div className="px-6 py-10 flex items-center gap-3 text-slate-500">
              <Activity size={18} className="animate-pulse" />
              <span className="text-sm">Loading…</span>
            </div>
          )}
          {!data && failed && (
            <div data-testid="status-load-error" className="px-6 py-10 flex flex-wrap items-center gap-4">
              <p className="text-sm text-slate-400">Could not load service status. Please try again.</p>
              <button
                data-testid="status-retry-btn"
                onClick={() => load()}
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#ED1C24] text-slate-300 hover:text-white text-sm px-5 py-2 transition-colors duration-300"
              >
                <RefreshCw size={14} /> Retry
              </button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
